import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  User, Mail, Phone, MapPin, Calendar,
  Globe, Shield, Bell, Lock, Edit2, Check, X,
  TrendingUp, Wallet, Star, Calendar as CalIcon,
  ChevronRight, Camera, FileText, Download, CheckCircle2,
  Upload, Eye, AlertCircle, Clock, ExternalLink, ChevronDown
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BankDetailsForm from '../BankDetailsForm';
import AgreementModal from '../AgreementModal';
import { API_URL as API } from '../../config/api';

const getDocUrl = (img) => {
  if (!img) return null;
  if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
  const baseUrl = API.replace(/\/api\/?$/, '');
  return `${baseUrl}${img.startsWith('/') ? '' : '/'}${img}`;
};

export default function PartnerProfile({ user, data, onTabChange }) {
  const { token, login, logout } = useAuth();
  const navigate = useNavigate();
  const kycFileInputRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [editEmail, setEditEmail] = useState(user?.email || '');
  const [editPhone, setEditPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFAMsg, setTwoFAMsg] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementLoading, setAgreementLoading] = useState(false);

  // KYC States
  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycForm, setKycForm] = useState({
    documentType: 'Aadhaar Card',
    documentNumber: '',
    documentImage: '',
    businessName: user?.name || '',
  });
  const [kycSaving, setKycSaving] = useState(false);
  const [kycError, setKycError] = useState('');
  const [kycSuccessMsg, setKycSuccessMsg] = useState('');
  const [uploadingKycFile, setUploadingKycFile] = useState(false);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);

  const fetchKyc = () => {
    if (!token) return;
    setKycLoading(true);
    axios.get(`${API}/kyc/my`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (res.data?.kyc) {
        setKycData(res.data.kyc);
        setKycForm({
          documentType: res.data.kyc.document_type || 'Aadhaar Card',
          documentNumber: res.data.kyc.document_number || '',
          documentImage: res.data.kyc.document_image || '',
          businessName: res.data.kyc.business_name || user?.name || '',
        });
      }
    })
    .catch(err => console.error("Error fetching partner KYC:", err))
    .finally(() => setKycLoading(false));
  };

  useEffect(() => {
    if (token) {
      setAgreementLoading(true);
      axios.get(`${API}/agreements/my`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        if (res.data.agreements && res.data.agreements.length > 0) {
          setAgreement(res.data.agreements[0]);
        }
      })
      .catch(err => console.error("Error fetching partner agreements:", err))
      .finally(() => setAgreementLoading(false));

      fetchKyc();
    }
  }, [token]);

  const confirmedBookings = data?.bookings?.filter(b => b.status === 'confirmed') || [];
  const totalEarnings = confirmedBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
  const walletBalance = Math.round(totalEarnings * 0.95);
  const primaryTurf = data?.turfs?.[0];
  const initials = (user?.name || 'Partner Owner').split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase();
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Not available';

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.toString().trim();
    if (clean.startsWith('+91')) {
      return clean;
    }
    if (clean.startsWith('91') && clean.length === 12) {
      return `+${clean}`;
    }
    return `+91 ${clean}`;
  };

  const handleSaveProfile = async () => {
    if (/\d/.test(editName)) {
      return setSaveError("Name cannot contain numeric values.");
    }
    setSaving(true);
    setSaveError('');
    try {
      const res = await axios.put(`${API}/owner/profile`, { name: editName, email: editEmail, phone: editPhone }, { headers: { Authorization: `Bearer ${token}` } });
      login({ ...res.data, _id: res.data.id }, token);
      setEditMode(false);
    } catch (err) {
      setSaveError(err.response?.data?.msg || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleKycFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setKycError('File exceeds 10MB limit. Please choose a smaller image.');
      return;
    }

    setUploadingKycFile(true);
    setKycError('');

    try {
      const formPayload = new FormData();
      formPayload.append('file', file);

      const res = await axios.post(`${API}/upload/document`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setKycForm(prev => ({
        ...prev,
        documentImage: res.data.url
      }));
    } catch (err) {
      console.error("KYC file upload error:", err);
      setKycError(err.response?.data?.msg || 'Failed to upload document photo.');
    } finally {
      setUploadingKycFile(false);
    }
  };

  const handleSaveKyc = async (e) => {
    e.preventDefault();
    setKycSaving(true);
    setKycError('');
    setKycSuccessMsg('');

    try {
      await axios.post(`${API}/kyc`, {
        document_type: kycForm.documentType,
        document_number: kycForm.documentNumber.trim(),
        document_image: kycForm.documentImage,
        business_name: kycForm.businessName || user?.name,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setKycSuccessMsg('KYC details submitted successfully for review!');
      fetchKyc();
      setTimeout(() => {
        setShowKycModal(false);
        setKycSuccessMsg('');
      }, 1500);
    } catch (err) {
      setKycError(err.response?.data?.msg || 'Failed to submit KYC details.');
    } finally {
      setKycSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeactivating(true);
    setSaveError('');
    try {
      await axios.post(`${API}/auth/delete-account`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      logout();
      alert('Your partner account has been deactivated successfully.');
      navigate('/partner/login');
    } catch (err) {
      setSaveError(err.response?.data?.msg || err.response?.data?.detail || 'Failed to deactivate account.');
      setShowDeleteModal(false);
    } finally {
      setDeactivating(false);
    }
  };

  const accountStats = [
    { label: 'Total Bookings', value: (data?.totalBookings || 0).toString(), sub: 'View all bookings', icon: <CalIcon size={20} />, color: '#084734' },
    { label: 'Total Earnings', value: `Rs.${totalEarnings.toLocaleString()}`, sub: 'View payouts', icon: <TrendingUp size={20} />, color: '#3b82f6' },
    { label: 'Wallet Balance', value: `Rs.${walletBalance.toLocaleString()}`, sub: 'Go to wallet', icon: <Wallet size={20} />, color: '#8b5cf6' },
    { label: 'Partner Rating', value: (data?.avgRating || 0).toString(), sub: 'View reviews', icon: <Star size={20} />, color: '#f59e0b' },
  ];

  const personalInfo = [
    { label: 'Full Name', value: user?.name || 'Partner Owner', icon: <User size={18} /> },
    { label: 'Email Address', value: user?.email || 'Not added', icon: <Mail size={18} /> },
    { label: 'Phone Number', value: user?.phone ? formatPhone(user.phone) : 'Not added', icon: <Phone size={18} /> },
    { label: 'Member Since', value: memberSince, icon: <Calendar size={18} /> },
    { label: 'Preferred Language', value: 'English', icon: <Globe size={18} /> },
    { label: 'Primary Venue', value: primaryTurf ? `${primaryTurf.location}, ${primaryTurf.city}` : 'No venue added', icon: <MapPin size={18} /> },
  ];

  const kycStatus = kycData?.status || 'not_submitted';

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>My Profile</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>Manage your personal information, KYC identity documents, and account settings.</p>
        </div>
        {editMode ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSaveProfile} disabled={saving} style={btnPrimary}><Check size={18} /> {saving ? 'Saving...' : 'Save Profile'}</button>
            <button onClick={() => { setEditMode(false); setSaveError(''); }} style={btnSecondary}><X size={18} /> Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setEditMode(true); setEditName(user?.name || ''); setEditEmail(user?.email || ''); setEditPhone(user?.phone || ''); }} style={btnSecondary}><Edit2 size={18} /> Edit Profile</button>
        )}
      </div>

      {saveError && (
        <div style={{ marginBottom: '1.5rem', padding: '12px 16px', borderRadius: '12px', background: '#fff1f2', border: '1.5px solid #fecdd3', color: '#be123c', fontWeight: '700', fontSize: '0.9rem' }}>
          {saveError}
        </div>
      )}

      {/* Top Banner Card */}
      <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', display: 'flex', gap: '3rem', alignItems: 'center', marginBottom: '2rem' }}>
         <div style={{ position: 'relative' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: '#161616', color: '#CEF17B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', fontWeight: '900', border: '4px solid #F8FAF7' }}>
               {initials}
            </div>
            <button style={{ position: 'absolute', bottom: '5px', right: '5px', width: '36px', height: '36px', borderRadius: '50%', background: 'white', border: '1.5px solid #EEF2E6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', cursor: 'pointer' }}>
               <Camera size={18} />
            </button>
         </div>

         <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '8px', flexWrap: 'wrap' }}>
               {editMode ? (
                 <input value={editName} onChange={e => setEditName(e.target.value)} style={{ fontSize: '1.4rem', fontWeight: '800', padding: '8px 14px', borderRadius: '10px', border: '1.5px solid #DCEFB8', outline: 'none' }} />
               ) : (
                 <h3 style={{ fontSize: '1.6rem', fontWeight: '800', margin: 0 }}>{user?.name || 'Partner Owner'}</h3>
               )}
               <span style={{ background: '#DCEFB8', color: '#084734', fontSize: '0.75rem', fontWeight: '800', padding: '4px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Shield size={12} /> Verified Partner
               </span>
               {kycStatus === 'approved' && (
                 <span style={{ background: '#DCFCE7', color: '#166534', fontSize: '0.75rem', fontWeight: '800', padding: '4px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                   <CheckCircle2 size={12} /> KYC Approved
                 </span>
               )}
            </div>
            {editMode ? (
               <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                 <input value={editEmail} onChange={e => setEditEmail(e.target.value)} type="email" placeholder="Business email" style={{ flex: 1, padding: '8px 14px', borderRadius: '10px', border: '1.5px solid #DCEFB8', outline: 'none', fontSize: '0.9rem', fontWeight: '600' }} />
                 <input value={editPhone} onChange={e => setEditPhone(e.target.value)} type="text" placeholder="Phone number" style={{ flex: 1, padding: '8px 14px', borderRadius: '10px', border: '1.5px solid #DCEFB8', outline: 'none', fontSize: '0.9rem', fontWeight: '600' }} />
               </div>
            ) : (
               <p style={{ color: '#374151', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px' }}>
                  <span>{user?.email || 'No email added'}</span>
                  <span>|</span>
                  <span>{user?.phone ? formatPhone(user.phone) : 'No phone added'}</span>
               </p>
            )}
            <p style={{ color: '#374151', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
               <MapPin size={16} /> {primaryTurf ? `${primaryTurf.city}, ${primaryTurf.location}` : 'No venue added'}
            </p>
         </div>

         <div style={{ display: 'flex', gap: '3rem', borderLeft: '1.5px solid #EEF2E6', paddingLeft: '3rem' }}>
            <div>
               <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>Partner ID</div>
               <div style={{ fontWeight: '800', color: '#161616' }}>{(user?.id || user?._id)?.slice(-8)?.toUpperCase() || 'NEW'} <Edit2 size={12} cursor="pointer" /></div>
               <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginTop: '12px', marginBottom: '4px' }}>Member Since</div>
               <div style={{ fontWeight: '800', color: '#161616' }}>{memberSince}</div>
            </div>
            <div>
               <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>Status</div>
               <div style={{ fontWeight: '800', color: '#084734' }}>Active</div>
               <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase', marginTop: '12px', marginBottom: '4px' }}>Last Login</div>
               <div style={{ fontWeight: '800', color: '#161616' }}>Current session</div>
            </div>
         </div>
      </div>

      {/* Info & Account Overview Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
         <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '2rem' }}>Personal Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
               {personalInfo.map((info, i) => (
                 <div key={i}>
                    <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                       {info.icon} {info.label}
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#161616' }}>{info.value}</div>
                 </div>
               ))}
            </div>
         </div>

         <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '2rem' }}>Account Overview</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
               {accountStats.map((s, i) => (
                 <div key={i} style={{ background: '#F8FAF7', padding: '1.5rem', borderRadius: '16px', border: '1.5px solid #EEF2E6' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'white', color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', border: '1px solid #EEF2E6' }}>
                       {s.icon}
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>{s.value}</div>
                    <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', marginBottom: '10px' }}>{s.label}</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                       {s.sub} <ChevronRight size={12} />
                    </div>
                 </div>
               ))}
            </div>
         </div>
      </div>

      {/* ── KYC VERIFICATION & DOCUMENTS SECTION ── */}
      <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#161616', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileText size={22} color="#084734" /> Partner KYC & Identity Documents
            </h3>
            <p style={{ color: '#374151', fontSize: '0.88rem', margin: 0, fontWeight: 500 }}>
              Government ID verification required for venue ownership validation and automated payouts.
            </p>
          </div>

          {/* Status Badge */}
          <div>
            {kycStatus === 'approved' && (
              <span style={{ background: '#DCFCE7', color: '#166534', fontSize: '0.82rem', fontWeight: '800', padding: '6px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={16} color="#166534" /> KYC Verified & Approved
              </span>
            )}
            {kycStatus === 'pending' && (
              <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: '0.82rem', fontWeight: '800', padding: '6px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={16} color="#92400E" /> Verification Under Review
              </span>
            )}
            {kycStatus === 'rejected' && (
              <span style={{ background: '#FEE2E2', color: '#991B1B', fontSize: '0.82rem', fontWeight: '800', padding: '6px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertCircle size={16} color="#991B1B" /> Verification Rejected
              </span>
            )}
            {kycStatus === 'not_submitted' && (
              <span style={{ background: '#F3F4F6', color: '#4B5563', fontSize: '0.82rem', fontWeight: '800', padding: '6px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                ⚠️ Not Submitted
              </span>
            )}
          </div>
        </div>

        {/* KYC Details Card */}
        <div style={{ background: '#F8FAF7', border: '1.5px solid #EEF2E6', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Document Type</div>
              <div style={{ fontWeight: '800', color: '#161616', fontSize: '1rem', marginTop: '4px' }}>
                {kycData?.document_type || 'Aadhaar Card'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Document Number</div>
              <div style={{ fontWeight: '800', color: '#161616', fontSize: '1rem', marginTop: '4px', fontFamily: 'monospace' }}>
                {kycData?.document_number || 'Not provided'}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Uploaded Photo</div>
              <div style={{ marginTop: '6px' }}>
                {kycData?.document_image ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={getDocUrl(kycData.document_image)}
                      alt="KYC Document"
                      onClick={() => setPreviewPhotoUrl(getDocUrl(kycData.document_image))}
                      style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px', border: '1.5px solid #CBD5E1', cursor: 'pointer' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <button
                      onClick={() => setPreviewPhotoUrl(getDocUrl(kycData.document_image))}
                      style={{ background: 'none', border: 'none', color: '#084734', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '4px', padding: 0 }}
                    >
                      <Eye size={14} /> View Photo
                    </button>
                  </div>
                ) : (
                  <span style={{ color: '#64748B', fontSize: '0.88rem', fontWeight: '600' }}>No image uploaded</span>
                )}
              </div>
            </div>

            {kycData?.admin_note && (
              <div style={{ gridColumn: '1 / -1', background: '#fff', padding: '10px 14px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                <span style={{ fontWeight: '800', fontSize: '0.8rem', color: '#084734' }}>Admin Note: </span>
                <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>{kycData.admin_note}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setKycForm({
                documentType: kycData?.document_type || 'Aadhaar Card',
                documentNumber: kycData?.document_number || '',
                documentImage: kycData?.document_image || '',
                businessName: kycData?.business_name || user?.name || '',
              });
              setKycError('');
              setKycSuccessMsg('');
              setShowKycModal(true);
            }}
            style={{
              background: '#084734',
              color: '#CEF17B',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 22px',
              fontWeight: '800',
              fontSize: '0.88rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Edit2 size={16} /> {kycData ? 'Update KYC Details & Photo' : 'Submit KYC Details & Photo'}
          </button>
        </div>
      </div>

      {/* Digitally Signed Partner Agreement Card */}
      <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#161616', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={22} color="#084734" /> Venue Partner Agreement & Merchant Contract
            </h3>
            <p style={{ color: '#374151', fontSize: '0.88rem', margin: 0, fontWeight: 500 }}>
              Master agreement covering venue booking fulfillments, platform commission, KYC bank details, and automated payout settlements.
            </p>
          </div>
          <span style={{ background: '#DCEFB8', color: '#084734', fontSize: '0.78rem', fontWeight: '800', padding: '6px 14px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle2 size={14} color="#084734" /> Digitally Signed Contract
          </span>
        </div>

        {agreement && (
          <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', borderRadius: '14px', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Certificate Number</div>
              <div style={{ fontWeight: '800', color: '#161616', fontSize: '0.95rem', marginTop: '2px' }}>{agreement.signature_id}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Digital Signature Date</div>
              <div style={{ fontWeight: '700', color: '#084734', fontSize: '0.9rem', marginTop: '2px' }}>
                {agreement.metadata?.signed_at_ist || new Date(agreement.signed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '700', textTransform: 'uppercase' }}>Agreement Version</div>
              <div style={{ fontWeight: '700', color: '#161616', fontSize: '0.9rem', marginTop: '2px' }}>v1.0 (Merchant Agreement)</div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowAgreementModal(true)}
            style={{
              background: '#084734',
              color: '#CEF17B',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 22px',
              fontWeight: '800',
              fontSize: '0.88rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FileText size={18} /> View Signed Contract
          </button>
          {agreement?.download_url && (
            <a
              href={agreement.download_url}
              target="_blank"
              rel="noreferrer"
              style={{
                background: 'none',
                border: '1.5px solid #084734',
                color: '#084734',
                borderRadius: '12px',
                padding: '12px 22px',
                fontWeight: '800',
                fontSize: '0.88rem',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download size={18} /> Download Signed PDF
            </a>
          )}
        </div>
      </div>

      {/* Bank Details Form */}
      <div style={{ marginTop: '2rem' }}>
        <BankDetailsForm token={token} required={true} />
      </div>

      {/* Security & Preferences */}
      <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', marginTop: '2rem' }}>
         <h3 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '2rem' }}>Security & Preferences</h3>

         {twoFAMsg && (
           <div style={{ marginBottom: '1rem', padding: '10px 14px', borderRadius: '10px', background: '#D1FAE5', border: '1px solid #86efac', color: '#065F46', fontWeight: '700', fontSize: '0.88rem' }}>
             {twoFAMsg}
           </div>
         )}

         <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem' }}>
            {/* Change Password */}
            <div
              onClick={() => onTabChange?.('settings', 'security')}
              style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', padding: '1.5rem', borderRadius: '20px', border: '1.5px solid #EEF2E6', cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#CEF17B'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#EEF2E6'}
            >
               <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#CEF17B10', color: '#CEF17B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Lock size={20} />
               </div>
               <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#161616' }}>Change Password</span>
                  <p style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500', marginTop: '4px' }}>Update your password regularly</p>
               </div>
               <ChevronRight size={18} style={{ color: '#374151' }} />
            </div>

            {/* Two-Factor Authentication */}
            <div
              style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', padding: '1.5rem', borderRadius: '20px', border: `1.5px solid ${twoFAEnabled ? '#CEF17B' : '#EEF2E6'}`, cursor: 'pointer', transition: '0.2s', background: twoFAEnabled ? '#F0FDF4' : 'white' }}
              onMouseEnter={e => { if (!twoFAEnabled) e.currentTarget.style.borderColor = '#CEF17B'; }}
              onMouseLeave={e => { if (!twoFAEnabled) e.currentTarget.style.borderColor = '#EEF2E6'; }}
            >
               <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#3b82f610', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={20} />
               </div>
               <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                     <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#161616' }}>Two-Factor Auth</span>
                     <span style={{ fontSize: '0.65rem', fontWeight: '800', background: twoFAEnabled ? '#DCEFB8' : '#EEF2E6', color: twoFAEnabled ? '#065F46' : '#374151', padding: '2px 8px', borderRadius: '6px' }}>
                       {twoFAEnabled ? 'Enabled' : 'Disabled'}
                     </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500', marginTop: '4px' }}>Extra layer of security</p>
               </div>
               {/* Toggle */}
               <div
                 onClick={() => {
                   setTwoFAEnabled(v => {
                     const next = !v;
                     setTwoFAMsg(next ? '2FA enabled. OTP will be sent on each login.' : '2FA has been disabled.');
                     setTimeout(() => setTwoFAMsg(''), 3000);
                     return next;
                   });
                 }}
                 style={{ width: '44px', height: '24px', background: twoFAEnabled ? '#CEF17B' : '#E5E7EB', borderRadius: '20px', padding: '2px', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
               >
                 <div style={{ position: 'absolute', top: '2px', left: twoFAEnabled ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
               </div>
            </div>

            {/* Notification Preferences */}
            <div
              onClick={() => onTabChange?.('settings', 'notifications')}
              style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', padding: '1.5rem', borderRadius: '20px', border: '1.5px solid #EEF2E6', cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#CEF17B'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#EEF2E6'}
            >
               <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#f59e0b10', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bell size={20} />
               </div>
               <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#161616' }}>Notification Preferences</span>
                  <p style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500', marginTop: '4px' }}>Manage how you receive alerts</p>
               </div>
               <ChevronRight size={18} style={{ color: '#374151' }} />
            </div>
         </div>
      </div>

      {/* Danger Zone / Delete Account Card */}
      <div style={{ background: '#FEF2F2', padding: '2rem', borderRadius: '24px', border: '1.5px solid #FCA5A5', marginTop: '2rem' }}>
         <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#991B1B', marginBottom: '0.5rem' }}>⚠️ Danger Zone</h3>
         <p style={{ color: '#7F1D1D', fontSize: '0.9rem', fontWeight: '500', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            Want to delete your partner account? This action will deactivate your partner profile, end your active session, and disable access to TurfX Partner portal.
         </p>
         <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{ background: '#DC2626', border: 'none', borderRadius: '12px', padding: '12px 24px', fontWeight: '800', fontSize: '0.9rem', color: 'white', cursor: 'pointer' }}
            >
              Deactivate Partner Account
            </button>
            <button
              onClick={() => navigate('/account-deletion')}
              style={{ background: 'none', border: '1.5px solid #FCA5A5', borderRadius: '12px', padding: '12px 20px', fontWeight: '700', fontSize: '0.88rem', color: '#991B1B', cursor: 'pointer' }}
            >
              View Deletion Guidelines →
            </button>
         </div>
      </div>

      {/* ── KYC EDIT / SUBMIT MODAL ── */}
      {showKycModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1.5rem', animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '520px', width: '100%',
            padding: '2rem', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#161616', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} color="#084734" /> Submit / Update KYC Documents
              </h3>
              <button onClick={() => setShowKycModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
                <X size={20} />
              </button>
            </div>

            {kycError && (
              <div style={{ background: '#fff1f2', color: '#be123c', padding: '10px 14px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '700' }}>
                {kycError}
              </div>
            )}
            {kycSuccessMsg && (
              <div style={{ background: '#D1FAE5', color: '#065F46', padding: '10px 14px', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '700' }}>
                {kycSuccessMsg}
              </div>
            )}

            <form onSubmit={handleSaveKyc} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              <div>
                <label style={modalLabelStyle}>KYC Document Type *</label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={kycForm.documentType}
                    onChange={e => setKycForm(f => ({ ...f, documentType: e.target.value }))}
                    style={{
                      ...modalInputStyle,
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      paddingRight: '38px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="Aadhaar Card">Aadhaar Card</option>
                    <option value="Passport">Passport</option>
                    <option value="Driving License">Driving License</option>
                    <option value="Voter ID">Voter ID</option>
                  </select>
                  <ChevronDown
                    size={18}
                    color="#084734"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={modalLabelStyle}>{kycForm.documentType} Number *</label>
                <input
                  type="text"
                  placeholder={`Enter ${kycForm.documentType} number`}
                  value={kycForm.documentNumber}
                  onChange={e => setKycForm(f => ({ ...f, documentNumber: e.target.value }))}
                  style={modalInputStyle}
                  required
                />
              </div>

              <div>
                <label style={modalLabelStyle}>Document Photo / Scan</label>
                <input
                  ref={kycFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/jpg,application/pdf"
                  onChange={handleKycFileUpload}
                  style={{ display: 'none' }}
                />

                {kycForm.documentImage ? (
                  <div style={{
                    border: '1.5px solid #DCEFB8', background: '#F4FBF1',
                    borderRadius: '12px', padding: '12px', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        src={kycForm.documentImage}
                        alt="KYC Document Preview"
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #DCEFB8' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#084734', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircle2 size={14} color="#084734" /> Document Photo Attached
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#64748B' }}>Saved in backend documents</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setKycForm(f => ({ ...f, documentImage: '' }));
                        if (kycFileInputRef.current) kycFileInputRef.current.value = '';
                      }}
                      style={{
                        background: '#FEE2E2', border: 'none', color: '#DC2626',
                        borderRadius: '8px', padding: '6px 10px', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: '700'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => kycFileInputRef.current?.click()}
                    style={{
                      border: '2px dashed #CBD5E1', borderRadius: '12px',
                      padding: '1.25rem', textAlign: 'center', cursor: 'pointer',
                      background: '#F8FAF7'
                    }}
                  >
                    <Upload size={24} color="#084734" style={{ margin: '0 auto 6px' }} />
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#084734' }}>
                      {uploadingKycFile ? 'Uploading photo...' : 'Click to select and upload document photo'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: '4px' }}>
                      JPEG, PNG, WebP, PDF (Max 10MB)
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                <button
                  type="submit"
                  disabled={kycSaving || uploadingKycFile}
                  style={{
                    flex: 1, background: '#084734', color: '#CEF17B', border: 'none',
                    padding: '12px', borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem',
                    cursor: (kycSaving || uploadingKycFile) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {kycSaving ? 'Submitting...' : '✓ Submit KYC Verification'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowKycModal(false)}
                  style={{
                    background: '#F3F4F6', color: '#374151', border: 'none',
                    padding: '12px 18px', borderRadius: '12px', fontWeight: '700', fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── PREVIEW PHOTO MODAL ── */}
      {previewPhotoUrl && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '1.5rem', animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', maxWidth: '720px', width: '100%',
            overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAF7', borderBottom: '1px solid #EEF2E6' }}>
              <div style={{ fontWeight: '800', color: '#084734', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} /> Uploaded KYC Document Photo
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <a
                  href={previewPhotoUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: '#E0F2FE', color: '#0369A1', borderRadius: '8px',
                    padding: '6px 12px', fontSize: '0.78rem', fontWeight: '700',
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <ExternalLink size={14} /> Open Full Size
                </a>
                <button
                  onClick={() => setPreviewPhotoUrl(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center' }}
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: '70vh', overflow: 'auto', background: '#0F172A' }}>
              <img
                src={previewPhotoUrl}
                alt="KYC Document Preview Full"
                style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1.5rem', animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '440px', width: '100%',
            padding: '2rem', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', margin: '0 auto 1.25rem' }}>
              ⚠️
            </div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#161616', margin: '0 0 0.5rem' }}>
              Deactivate Partner Account?
            </h3>
            <p style={{ color: '#4B5563', fontSize: '0.92rem', lineHeight: 1.5, marginBottom: '1.75rem', fontWeight: 500 }}>
              Are you sure you want to deactivate your partner account? You will be logged out and your venue listings will be suspended.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deactivating}
                style={{
                  flex: 1, background: '#DC2626', color: 'white', border: 'none',
                  padding: '12px', borderRadius: '12px', fontWeight: 800, fontSize: '0.92rem',
                  cursor: deactivating ? 'not-allowed' : 'pointer', opacity: deactivating ? 0.7 : 1
                }}
              >
                {deactivating ? 'Deactivating...' : 'Yes, Deactivate'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deactivating}
                style={{
                  flex: 1, background: '#F3F4F6', color: '#374151', border: 'none',
                  padding: '12px', borderRadius: '12px', fontWeight: 700, fontSize: '0.92rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        agreementType="partner"
        userAgreement={agreement}
      />
    </div>
  );
}

const btnPrimary = {
  background: '#084734',
  color: '#CEF17B',
  border: 'none',
  padding: '10px 18px',
  borderRadius: '10px',
  fontWeight: '800',
  fontSize: '0.85rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const btnSecondary = {
  background: 'white',
  border: '1.5px solid #EEF2E6',
  color: '#374151',
  padding: '10px 18px',
  borderRadius: '10px',
  fontWeight: '700',
  fontSize: '0.85rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const modalLabelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#374151',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const modalInputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1.5px solid #E5E7EB',
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#111827',
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box'
};
